import { Calendar } from "@components/ui/Calendar"
import { Checkbox } from "@components/ui/Checkbox"
import { Input } from "@components/ui/Input"
import { BrowseFilmTileFragment, EventType, FilmSessionFragment, UseProgramFilmsQuery } from "@generated-types"
import { formatDateUri, formatNiceDate } from "@utils/date-format"
import { Command } from "cmdk"
import { Fragment, ReactNode } from "react"
import { FilmSearch, FilmSearchMode } from "./useFilmSearch"
import { Spinner } from "@components/ui/Spinner"
import dayjs from "dayjs"
import { isPastEvent } from "@utils/past-event"

export function FilmFilter<K extends FilmFilterKey = any>(props: {
  filterKey?: K
  browse: FilmSearch
  mode?: FilmSearchMode
  store?: {
    value: FilmFilterValue<K>
    setValue(value: FilmFilterValue<K>): void
  }
}) {
  const def = FILM_FILTERS[props.filterKey!]

  const value = props.store ? props.store.value : (props.browse.filters.get(props.filterKey!) as any)
  const set = props.store ? props.store.setValue : (props.browse.filters.setter(props.filterKey!) as any)

  if (!def.render) {
    return null
  }

  if (props.browse.status !== "success") {
    return (
      <div className="p-4 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return def.render!({
    mode: props.mode!,
    value: value,
    onValueChange: set,
    options: props.browse.filterOptions[props.filterKey!] as any,
  })
}

const EVENT_TYPE_LABELS = [
  { label: "Films", type: EventType.Film },
  { label: "Events", type: EventType.Event },
  { label: "Short Films", type: EventType.Short },
] as { label: string; type: EventType }[]

export function getEventTypesLabel(types: string[]) {
  const names = EVENT_TYPE_LABELS.filter((type) => types.includes(type.type)).map((type) => type.label)
  let label = names.join(", ").replace(/, ([^,]*)$/, " & $1")
  if (label === "Films, Events & Short Films") label = "Include Short Films"
  if (names.length === 1) label = names[0] + " Only"
  return label
}

export const FILM_FILTERS = {
  venues: filter({
    name: "Venue",
    popupTitle: "Filter by Venue",
    getDefault: () => [] as string[],
    getOptions: (films, lookup) => {
      const venuesInUse = new Set<string>()
      for (const film of films) {
        for (const session of film.sessions ?? []) {
          if (session?.info?.venue?.parentSlug) {
            venuesInUse.add(session.info.venue.parentSlug)
          }
        }
      }
      const districts: { label: string; slug: string; venues: { label: string; value: string }[] }[] = []
      for (const venue of lookup.currentProgram?.venues ?? []) {
        if (!venue!.district?.name) continue
        const district = venue!.district?.name
        let districtObj = districts.find((d) => d.label === district)
        if (!districtObj) {
          districtObj = { label: venue?.district?.name!, slug: venue?.district?.slug!, venues: [] }
          districts.push(districtObj)
        }
        if (venuesInUse.has(venue?.slug!)) {
          districtObj.venues.push({ label: venue?.title!, value: venue?.slug! })
        }
      }
      return districts.filter((d) => d.venues.length > 0)
    },
    getMatchers(values, opts) {
      const slugs = new Set()
      for (const value of values) {
        const district = opts.find((d) => d.slug === value)
        if (district) {
          district.venues.forEach((venue) => slugs.add(venue.value))
        } else {
          slugs.add(value)
        }
      }
      return {
        matchSession(session) {
          return slugs.has(session.info?.venue?.parentSlug)
        },
      }
    },
    getFilterTags(values, opts) {
      const tags: FilterTag[] = []
      const venues = opts.flatMap((d) => d.venues)
      for (const value of values) {
        const district = opts.find((d) => d.slug === value)
        if (district) {
          tags.push({
            type: "District",
            label: district.label,
            key: "venues",
            value: district.slug,
          })
          continue
        }
        const venue = venues.find((v) => v.value === value)
        if (venue) {
          tags.push({
            type: "Venue",
            label: venue.label,
            key: "venues",
            value: venue.value,
          })
        } else {
          tags.push({
            type: "Venue",
            label: value,
            key: "venues",
            value,
          })
        }
      }
      return tags
    },
    render({ value, onValueChange, options }) {
      const update = (fn: (items: Set<string>) => void) => {
        const items = new Set(value)
        fn(items)
        onValueChange(Array.from(items))
      }

      return (
        <>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((district) => {
              const districtActive = value?.includes(district.slug)
              const districtSomeActive = value?.some((v) => district.venues.some((venue) => venue.value === v))
              const onSelectDistrict = () => {
                if (districtActive || districtSomeActive) {
                  update((items) => {
                    items.delete(district.slug)
                    district.venues.forEach((venue) => items.delete(venue.value))
                  })
                } else {
                  update((items) => {
                    district.venues.forEach((venue) => items.delete(venue.value))
                    items.add(district.slug)
                  })
                }
              }
              return (
                <Fragment key={district.slug}>
                  <Command.Item
                    key={district.slug}
                    aria-checked={districtActive || districtSomeActive ? true : undefined}
                    onSelect={onSelectDistrict}
                  >
                    <Checkbox checked={districtActive || districtSomeActive} />
                    {district.label}
                  </Command.Item>
                  {district.venues.map((venue) => {
                    const active = value?.includes(venue.value) || districtActive
                    const onSelect = () => {
                      if (active) {
                        update((items) => {
                          if (items.has(district.slug)) {
                            district.venues.forEach((venue) => items.add(venue.value))
                            items.delete(district.slug)
                            items.delete(venue.value)
                          } else {
                            items.delete(venue.value)
                          }
                        })
                      } else {
                        update((items) => {
                          if (items.has(district.slug)) {
                            items.delete(district.slug)
                          }
                          items.add(venue.value)
                        })
                      }
                    }
                    return (
                      <Command.Item key={venue.value} aria-checked={active ? true : undefined} onSelect={onSelect}>
                        <span className="ml-3">
                          <Checkbox checked={active} />
                        </span>
                        {venue.label}
                      </Command.Item>
                    )
                  })}
                </Fragment>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  date: filter({
    name: "Date",
    popupTitle: "Filter by Date",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      const dates = getUniqueSorted(
        items.flatMap((film) => film?.sessions?.flatMap((session) => session?.info?.dateTime?.substring(0, 10)) || []),
      )
      return {
        dates: dates,
        pastDates: dates.filter((date) => dayjs(date).isBefore(dayjs(), "day")),
        hasPastEvents: items.some((film) => isPastEvent({ film })),
        hasFutureEvents: items.some((film) => !isPastEvent({ film })),
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => {
        if (value === "past") {
          return {
            type: "Including",
            label: "Past Events",
            key: "date",
            value,
            all: true,
          }
        } else {
          return {
            type: "Date",
            label: formatNiceDate(value),
            key: "date",
            value,
          }
        }
      })
    },
    getMatchers(values, options) {
      const dateValues = values.filter((v) => v !== "past")
      const includePastDates =
        values.includes("past") || values.some((v) => options.pastDates.includes(v)) || !options.hasFutureEvents

      return {
        matchSession(session) {
          if (!includePastDates && isPastEvent({ session })) {
            return false
          }
          if (dateValues.length > 0) {
            return dateValues.includes(session.info?.dateTime?.substring(0, 10) ?? "unknown")
          }
          return true
        },
        always: true,
      }
    },
    render({ value, onValueChange, options }) {
      const dates = value?.map((date) => new Date(date)) ?? []
      const includePast = value?.includes("past") ?? false
      const setDates = (dates: Date[]) => {
        onValueChange([...new Set(dates.map((date) => formatDateUri(date)).sort()), ...(includePast ? ["past"] : [])])
      }

      const firstDate = new Date(options.dates.at(0)!)
      const lastDate = new Date(options.dates.at(-1)!)

      return (
        <div>
          <Calendar
            mode="multiple"
            onSelect={(dates) => {
              setDates(dates ?? [])
            }}
            defaultMonth={firstDate}
            startMonth={firstDate}
            endMonth={lastDate}
            disabled={[{ before: firstDate, after: lastDate }, (date) => !options.dates.includes(formatDateUri(date))]}
            selected={dates}
            footer={
              options.hasFutureEvents &&
              options.hasPastEvents && (
                <Checkbox
                  checked={value?.includes("past")}
                  label="Include past events"
                  onCheckedChange={(checked) => {
                    onValueChange([...(value?.filter((item) => item !== "past") ?? []), ...(checked ? ["past"] : [])])
                  }}
                />
              )
            }
          />
        </div>
      )
    },
  }),
  genre: filter({
    name: "Genre",
    popupTitle: "Filter by Genre",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      return getUniqueSorted(items.flatMap((film) => film?.filmInfo?.genre || []))
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          if (film.filmInfo?.genre) {
            return values.some((value) => film.filmInfo?.genre?.includes(value))
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Genre",
        label: value,
        key: "genre",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <div cmdk-input-wrapper="">
            <Command.Input asChild>
              <Input type="search" placeholder="Filter by Genre" leftIcon="search" className="w-full" />
            </Command.Input>
          </div>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((genre) => {
              const active = value?.includes(genre)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== genre))
                } else {
                  onValueChange([...(value || []), genre])
                }
              }
              return (
                <Command.Item key={genre} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {genre}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  strand: filter({
    name: "Program Strand",
    popupTitle: "Filter by Program Strand",
    getDefault: () => [] as string[],
    getOptions: (items, query) => {
      const usedSlugs = new Set<string>()
      for (const film of items) {
        if (film.strands?.length) {
          for (const strand of film.strands) {
            usedSlugs.add(strand?.slug ?? "")
          }
        }
      }
      return (
        query.strands?.nodes
          .map((strand) => ({ label: strand.title!, slug: strand.slug! }))
          .filter((strand) => usedSlugs.has(strand.slug)) ?? []
      )
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          return film.strands?.some((strand) => values.includes(strand?.slug!)) ?? false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Strand",
        label: opts.find((s) => s.slug === value)?.label ?? value,
        key: "strand",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <div cmdk-input-wrapper="">
            <Command.Input asChild>
              <Input type="search" placeholder="Filter by Strand" leftIcon="search" className="w-full" />
            </Command.Input>
          </div>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((strand) => {
              const active = value?.includes(strand.slug)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== strand.slug))
                } else {
                  onValueChange([...(value || []), strand.slug])
                }
              }
              return (
                <Command.Item key={strand.slug} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {strand.label}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  country: filter({
    name: "Country",
    popupTitle: "Filter by Country",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      return getUniqueSorted(items.flatMap((film) => film?.filmInfo?.primaryCountry || []))
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          if (film.filmInfo?.primaryCountry) {
            return values.some((value) => film.filmInfo?.primaryCountry === value)
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Country",
        label: value,
        key: "country",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <div cmdk-input-wrapper="">
            <Command.Input asChild>
              <Input type="search" placeholder="Filter by Country" leftIcon="search" className="w-full" />
            </Command.Input>
          </div>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((genre) => {
              const active = value?.includes(genre)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== genre))
                } else {
                  onValueChange([...(value || []), genre])
                }
              }
              return (
                <Command.Item key={genre} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {genre}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  language: filter({
    name: "Language",
    popupTitle: "Filter by Language",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      return getUniqueSorted(items.flatMap((film) => film?.filmInfo?.primaryLanguage || []))
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          if (film.filmInfo?.primaryLanguage) {
            return values.some((value) => film.filmInfo?.primaryLanguage === value)
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Language",
        label: value,
        key: "language",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <div cmdk-input-wrapper="">
            <Command.Input asChild>
              <Input type="search" placeholder="Filter by Language" leftIcon="search" className="w-full" />
            </Command.Input>
          </div>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((language) => {
              const active = value?.includes(language)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== language))
                } else {
                  onValueChange([...(value || []), language])
                }
              }
              return (
                <Command.Item key={language} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {language}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  classification: filter({
    name: "Classification",
    popupTitle: "Filter by Classification",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      return getUniqueSorted(items.flatMap((film) => film?.eventInfo?.classification || []))
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          if (film.eventInfo?.classification) {
            return values.some((value) => film.eventInfo?.classification === value)
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Classification",
        label: value,
        key: "classification",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((language) => {
              const active = value?.includes(language)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== language))
                } else {
                  onValueChange([...(value || []), language])
                }
              }
              return (
                <Command.Item key={language} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {language}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  director: filter({
    name: "Director",
    popupTitle: "Filter by Director",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      return getUniqueSorted(items.flatMap((film) => film?.credits?.director || []))
    },
    getMatchers(values) {
      return {
        matchFilm(film) {
          if (film.credits?.director?.some((director) => values.includes(director!))) {
            return true
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Director",
        label: value,
        key: "director",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <div cmdk-input-wrapper="">
            <Command.Input asChild>
              <Input type="search" placeholder="Filter by Director" leftIcon="search" className="w-full" />
            </Command.Input>
          </div>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((director) => {
              const active = value?.includes(director)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== director))
                } else {
                  onValueChange([...(value || []), director])
                }
              }
              return (
                <Command.Item key={director} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {director}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  access: filter({
    name: "Accessibility",
    popupTitle: "Filter by Accessibility",
    getDefault: () => [] as string[],
    getOptions: (items) => {
      const values = new Set<string>()
      for (const film of items) {
        for (const session of film.sessions ?? []) {
          if (session?.info?.access) {
            for (const access of session.info.access) {
              if (access) {
                values.add(access)
              }
            }
          }
        }
      }
      return [...values].sort()
    },
    getMatchers(values) {
      return {
        matchSession(session) {
          for (const access of session.info?.access ?? []) {
            for (const value of values) {
              if (access === value) return true
            }
          }
          return false
        },
      }
    },
    getFilterTags(values, opts) {
      return values.map((value) => ({
        type: "Access",
        label: value,
        key: "access",
        value,
      }))
    },
    render({ value, onValueChange, options }) {
      return (
        <>
          <Command.List>
            <Command.Empty>No results found.</Command.Empty>
            {options.map((language) => {
              const active = value?.includes(language)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== language))
                } else {
                  onValueChange([...(value || []), language])
                }
              }
              return (
                <Command.Item key={language} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {language}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
  type: filter({
    name: "Type",
    popupTitle: "Filter by Type",
    getDefault: () => [EventType.Film, EventType.Event] as string[],
    getOptions: (items) => {
      const types = new Set<EventType>()
      for (const film of items) {
        if (!film.eventInfo?.eventType) continue
        types.add(film.eventInfo?.eventType)
      }
      return {
        eventTypes: EVENT_TYPE_LABELS.filter((type) => types.has(type.type)),
      }
    },
    getMatchers(values) {
      let matchFilm = (film: BrowseFilmTileFragment) => values.includes(film.eventInfo?.eventType!)
      if (values.length === 0) {
        matchFilm = (film) =>
          film.eventInfo?.eventType === EventType.Film || film.eventInfo?.eventType === EventType.Event
      }
      return {
        matchFilm,
        always: true,
      }
    },
    getFilterTags(values, opts) {
      const tags: FilterTag[] = []
      const label = getEventTypesLabel(values)
      if (label !== "Films & Events") {
        tags.push({
          type: "Showing",
          label: label,
          key: "type",
          all: true,
        })
      }
      return tags
    },
    normalizeValue(value) {
      if (!value.length) {
        return [EventType.Film, EventType.Event]
      }
      return value
    },
    render({ value, onValueChange, options }) {
      const onPastToggle = () => {
        if (value?.includes("past")) {
          onValueChange(value?.filter((g) => g !== "past"))
        } else {
          onValueChange([...(value || []), "past"])
        }
      }

      return (
        <>
          <Command.List>
            {options.eventTypes.map((option) => {
              const active = value?.includes(option.type)
              const onSelect = () => {
                if (active) {
                  onValueChange(value?.filter((g) => g !== option.type))
                } else {
                  onValueChange([...(value || []), option.type])
                }
              }
              return (
                <Command.Item key={option.type} aria-checked={active ? true : undefined} onSelect={onSelect}>
                  <Checkbox checked={active} onCheckedChange={onSelect} />
                  {option.label}
                </Command.Item>
              )
            })}
          </Command.List>
        </>
      )
    },
  }),
} satisfies {
  [K: string]: FilmSearchFilter<any, any>
}
type FilmSearchFilter<TValue, TOptions> = {
  name: string
  popupTitle?: string
  getDefault: () => TValue
  getOptions: (items: BrowseFilmTileFragment[], query: UseProgramFilmsQuery, mode: FilmSearchMode) => TOptions
  getFilterTags?: (value: NoInfer<TValue>, options: NoInfer<TOptions>) => FilterTag[]
  getMatchers?: (
    value: NoInfer<TValue>,
    options: NoInfer<TOptions>,
  ) => {
    matchFilm?(film: BrowseFilmTileFragment): boolean
    matchSession?(session: FilmSessionFragment): boolean
    always?: boolean
  }
  render?(args: {
    mode: FilmSearchMode
    value: TValue | undefined
    onValueChange: (value: TValue | undefined) => void
    options: TOptions
    popover?: boolean
  }): ReactNode
  normalizeValue?: (value: TValue) => TValue
}

function filter<TValue, TOptions>(def: FilmSearchFilter<TValue, TOptions>) {
  return def
}

function getUniqueSorted<T>(arr: T[] | null | undefined): Exclude<T, null | undefined>[] {
  return (new Set(arr).size ? Array.from(new Set(arr)).sort() : []) as any
}

export type FilmFilterKey = keyof typeof FILM_FILTERS
export type FilterDef<T extends FilmFilterKey> = (typeof FILM_FILTERS)[T]
export type FilmFilterValue<T extends FilmFilterKey> = FilterDef<T> extends FilmSearchFilter<infer V, any> ? V : never
export type FilmFilterOptions<T extends FilmFilterKey> = FilterDef<T> extends FilmSearchFilter<any, infer O> ? O : never
export type FilmFilterValues = {
  [K in FilmFilterKey]?: FilmFilterValue<K>
}

export type FilterTag = {
  type: string
  label: string
  key: string
  value?: string
  all?: boolean
}

export const DEFAULT_FILM_FILTERS = Object.fromEntries(
  Object.entries(FILM_FILTERS).map(([key, def]) => [key, def.getDefault()]),
) as { [K in FilmFilterKey]: FilmFilterValue<K> }
